import { Link } from "react-router-dom";
import { Phone, MapPin, Clock, Instagram, Facebook, Youtube, MessageCircle, ChevronRight } from "lucide-react";
import { BRAND, NAV_LINKS, SERVICES, buildWhatsAppLink } from "@/data/site";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="relative bg-[#050A1F] border-t border-white/5 overflow-hidden"
    >
      <div className="absolute inset-0 bg-blueprint opacity-30 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[600px] h-[600px] rounded-full bg-inci-blue/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] rounded-full bg-gold/5 blur-[100px] pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-10">
        {/* Mega CTA */}
        <div className="mb-16 text-center">
          <p className="text-gold uppercase tracking-[0.4em] text-xs mb-3">
            Bizimle iletişime geçin
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl font-extrabold leading-none text-balance">
            <span className="text-gradient-silver">Aracınız için </span>
            <span className="text-gradient-gold">en doğru servis.</span>
          </h2>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="footer-call-btn"
              className="gradient-gold text-black font-semibold px-7 py-3.5 inline-flex items-center gap-2 hover:shadow-[0_0_30px_rgba(212,175,55,0.4)] transition-all"
            >
              <Phone className="w-4 h-4" /> {BRAND.phone}
            </a>
            <a
              href={buildWhatsAppLink()}
              target="_blank"
              rel="noreferrer"
              data-testid="footer-whatsapp-btn"
              className="bg-[#25D366] text-white font-semibold px-7 py-3.5 inline-flex items-center gap-2 hover:bg-[#1ebd5a] transition-all"
            >
              <MessageCircle className="w-4 h-4" /> WhatsApp ile Yaz
            </a>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center gap-3 mb-5">
              <img src={BRAND.logo} alt="Star Otogaz" className="h-14 w-auto" />
            </Link>
            <p className="text-white/60 text-sm leading-relaxed mb-5">
              Eskişehir Tepebaşı'nda LPG sistemleri ve İnci Akü yetkili
              bayisi. Güvenli sürüş, maksimum performans.
            </p>
            <div className="flex items-center gap-3">
              <a
                href={BRAND.social.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="Instagram"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 hover:border-gold hover:text-gold transition-all"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href={BRAND.social.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Facebook"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 hover:border-gold hover:text-gold transition-all"
              >
                <Facebook className="w-4 h-4" />
              </a>
              <a
                href={BRAND.social.youtube}
                target="_blank"
                rel="noreferrer"
                aria-label="Youtube"
                className="w-10 h-10 flex items-center justify-center rounded-full border border-white/10 hover:border-gold hover:text-gold transition-all"
              >
                <Youtube className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Menu */}
          <div>
            <h4 className="font-display font-semibold text-white mb-5 text-sm uppercase tracking-[0.2em]">
              Menü
            </h4>
            <ul className="space-y-3">
              {NAV_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-white/65 hover:text-gold text-sm inline-flex items-center gap-2 group transition-colors"
                  >
                    <ChevronRight className="w-3 h-3 text-gold opacity-0 -ml-3 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-display font-semibold text-white mb-5 text-sm uppercase tracking-[0.2em]">
              Hizmetler
            </h4>
            <ul className="space-y-3">
              {SERVICES.slice(0, 6).map((s) => (
                <li key={s.slug}>
                  <Link
                    to="/hizmetlerimiz"
                    className="text-white/65 hover:text-gold text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact + Map */}
          <div>
            <h4 className="font-display font-semibold text-white mb-5 text-sm uppercase tracking-[0.2em]">
              İletişim
            </h4>
            <ul className="space-y-3 text-sm text-white/70 mb-5">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
                <span>{BRAND.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gold flex-shrink-0" />
                <a href={`tel:${BRAND.phoneTel}`} className="hover:text-gold transition-colors">
                  {BRAND.phone}
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-gold flex-shrink-0" />
                <span>{BRAND.workingHours}</span>
              </li>
            </ul>
            <div className="rounded-lg overflow-hidden border border-white/10 h-32">
              <iframe
                title="Star Otogaz Konum"
                src={BRAND.mapsEmbed}
                width="100%"
                height="100%"
                style={{ border: 0, filter: "grayscale(0.5) invert(0.85)" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          </div>
        </div>

        <div className="pt-8 border-t border-white/10 flex flex-col md:flex-row gap-3 items-center justify-between text-xs text-white/45">
          <p>
            © {new Date().getFullYear()} {BRAND.fullName}. Tüm hakları saklıdır.
          </p>
          <p>Güvenli Sürüş, Maksimum Performans.</p>
        </div>
      </div>
    </footer>
  );
}
